import type {
  BusinessFunction,
  DerivedContext,
  Skill,
} from '../snapshot/data-views.ts';

/**
 * Pure operations over a platform-map draft. The platform-map
 * intersection is the derivedContext↔business-function adjacency edge —
 * `DraftDerivedContext.functions` holds the ids of the business functions a
 * derivedContext serves. Every op returns a new framework (no mutation), so
 * they're trivially unit-testable; the React state + id minting + dirty
 * tracking live in {@link usePlatformMapDraft}.
 */

export interface DraftSkill {
  id: string;
  display: string;
  kind: Skill['kind'];
  isNew?: boolean;
}

export interface DraftDerivedContext {
  id: string;
  display: string;
  subtitle: string | null;
  skills: DraftSkill[];
  /** Business-function ids this derivedContext serves (the adjacency edges). */
  functions: string[];
  isNew?: boolean;
}

export interface DraftBusinessFunction {
  id: string;
  display: string;
  /** Maps to the business function's `subtitle` (its short description). */
  subtitle: string | null;
  /**
   * Parent business function id (same id space as `id`) for sub-grouping,
   * or null for a top-level function. Kept to a single level (a parent is
   * always top-level) so the roll-up reads at a glance.
   */
  parentFunctionId: string | null;
  isNew?: boolean;
}

export interface PlatformMapFramework {
  derivedContexts: DraftDerivedContext[];
  businessFunctions: DraftBusinessFunction[];
}

export function seedFramework(
  derivedContexts: DerivedContext[],
  functions: BusinessFunction[],
): PlatformMapFramework {
  return {
    derivedContexts: derivedContexts.map((c) => ({
      id: c.id,
      display: c.display,
      subtitle: c.subtitle ?? null,
      functions: [...c.functions],
      skills: c.skills.map((s) => ({ id: s.id, display: s.display, kind: s.kind })),
    })),
    businessFunctions: functions.map((f) => ({
      id: f.id,
      display: f.display,
      subtitle: f.subtitle ?? null,
      parentFunctionId: f.parent_function_id ?? null,
    })),
  };
}

function mapComp(
  fw: PlatformMapFramework,
  id: string,
  fn: (c: DraftDerivedContext) => DraftDerivedContext,
): PlatformMapFramework {
  return {
    ...fw,
    derivedContexts: fw.derivedContexts.map((c) => (c.id === id ? fn(c) : c)),
  };
}

export function renameDerivedContext(
  fw: PlatformMapFramework,
  id: string,
  display: string,
): PlatformMapFramework {
  return mapComp(fw, id, (c) => ({ ...c, display }));
}

export function deleteDerivedContext(
  fw: PlatformMapFramework,
  id: string,
): PlatformMapFramework {
  return { ...fw, derivedContexts: fw.derivedContexts.filter((c) => c.id !== id) };
}

export function addDerivedContext(
  fw: PlatformMapFramework,
  id: string,
  display: string,
): PlatformMapFramework {
  return {
    ...fw,
    derivedContexts: [
      ...fw.derivedContexts,
      { id, display, subtitle: null, skills: [], functions: [], isNew: true },
    ],
  };
}

export function renameSkill(
  fw: PlatformMapFramework,
  compId: string,
  skillId: string,
  display: string,
): PlatformMapFramework {
  return mapComp(fw, compId, (c) => ({
    ...c,
    skills: c.skills.map((s) => (s.id === skillId ? { ...s, display } : s)),
  }));
}

export function deleteSkill(
  fw: PlatformMapFramework,
  compId: string,
  skillId: string,
): PlatformMapFramework {
  return mapComp(fw, compId, (c) => ({
    ...c,
    skills: c.skills.filter((s) => s.id !== skillId),
  }));
}

export function addSkill(
  fw: PlatformMapFramework,
  compId: string,
  skillId: string,
  display: string,
): PlatformMapFramework {
  return mapComp(fw, compId, (c) => ({
    ...c,
    skills: [...c.skills, { id: skillId, display, kind: 'framework', isNew: true }],
  }));
}

export function renameBusinessFunction(
  fw: PlatformMapFramework,
  id: string,
  display: string,
): PlatformMapFramework {
  return {
    ...fw,
    businessFunctions: fw.businessFunctions.map((f) =>
      f.id === id ? { ...f, display } : f,
    ),
  };
}

export function editBusinessFunctionDescription(
  fw: PlatformMapFramework,
  id: string,
  subtitle: string,
): PlatformMapFramework {
  return {
    ...fw,
    businessFunctions: fw.businessFunctions.map((f) =>
      f.id === id ? { ...f, subtitle: subtitle.trim() || null } : f,
    ),
  };
}

export function deleteBusinessFunction(
  fw: PlatformMapFramework,
  id: string,
): PlatformMapFramework {
  return {
    ...fw,
    businessFunctions: fw.businessFunctions
      .filter((f) => f.id !== id)
      // Orphan any children of the deleted function (drop the dangling
      // parent pointer) so no function points at a parent that's gone.
      .map((f) => (f.parentFunctionId === id ? { ...f, parentFunctionId: null } : f)),
    // Drop the now-dangling edge from every derivedContext.
    derivedContexts: fw.derivedContexts.map((c) => ({
      ...c,
      functions: c.functions.filter((fnId) => fnId !== id),
    })),
  };
}

export function addBusinessFunction(
  fw: PlatformMapFramework,
  id: string,
  display: string,
): PlatformMapFramework {
  return {
    ...fw,
    businessFunctions: [
      ...fw.businessFunctions,
      { id, display, subtitle: null, parentFunctionId: null, isNew: true },
    ],
  };
}

/**
 * Set (or clear, when `parentId` is null) a business function's parent for
 * sub-grouping. A function can't be its own parent; the picker only offers
 * top-level functions as parents (single-level hierarchy), so cycles can't
 * form. This guards self-parenting defensively.
 */
export function setBusinessFunctionParent(
  fw: PlatformMapFramework,
  id: string,
  parentId: string | null,
): PlatformMapFramework {
  if (parentId === id) return fw;
  return {
    ...fw,
    businessFunctions: fw.businessFunctions.map((f) =>
      f.id === id ? { ...f, parentFunctionId: parentId } : f,
    ),
  };
}

/**
 * Order business functions for the grouped/nested view: each top-level
 * function is immediately followed by its children, so the grid clusters
 * a group's columns together. Standalone functions keep their relative
 * place; a child whose parent is missing is appended (defensive — the
 * picker prevents dangling parents). Pure; input is not mutated.
 */
export function orderByParent(
  fns: DraftBusinessFunction[],
): DraftBusinessFunction[] {
  const childrenOf = new Map<string, DraftBusinessFunction[]>();
  const topLevel: DraftBusinessFunction[] = [];
  for (const f of fns) {
    if (f.parentFunctionId == null) {
      topLevel.push(f);
    } else {
      const arr = childrenOf.get(f.parentFunctionId) ?? [];
      arr.push(f);
      childrenOf.set(f.parentFunctionId, arr);
    }
  }
  const out: DraftBusinessFunction[] = [];
  const seen = new Set<string>();
  for (const p of topLevel) {
    out.push(p);
    seen.add(p.id);
    for (const c of childrenOf.get(p.id) ?? []) {
      out.push(c);
      seen.add(c.id);
    }
  }
  for (const f of fns) if (!seen.has(f.id)) out.push(f);
  return out;
}

export function toggleIntersection(
  fw: PlatformMapFramework,
  compId: string,
  fnId: string,
): PlatformMapFramework {
  return mapComp(fw, compId, (c) => ({
    ...c,
    functions: c.functions.includes(fnId)
      ? c.functions.filter((f) => f !== fnId)
      : [...c.functions, fnId],
  }));
}
